import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { Program, DatabaseProgram } from "@/types/program";
import type { Json } from "@/integrations/supabase/types";

export function useProgramsFetch() {
  const { toast } = useToast();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPrograms = async () => {
    try {
      setIsLoading(true);
      
      // Force a fresh authentication check
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error("Auth error:", authError);
        setIsLoading(false);
        return;
      }
      
      if (!user) {
        console.log("No authenticated user found");
        setIsLoading(false);
        return;
      }

      console.log("Current authenticated user:", user.id);

      // Add explicit WHERE clause as a safety measure and log the query
      console.log("Fetching programs with explicit coach_id filter:", user.id);
      
      const { data, error } = await supabase
        .from('programs')
        .select('program_id, program_data, ai_context, client_id, name, coach_id')
        .eq('coach_id', user.id); // Explicit filter as safety measure

      console.log("Query result - error:", error);
      console.log("Query result - data count:", data?.length || 0);
      console.log("Raw data from query:", data);

      if (error) {
        console.error("Error fetching programs:", error);
        toast({
          title: "Error fetching programs",
          description: error.message,
          variant: "destructive"
        });
        return;
      }

      if (!data) {
        console.log("No programs data returned");
        setPrograms([]);
        return;
      }

      // Log each program's coach_id vs current user
      data.forEach((program, index) => {
        console.log(`Program ${index + 1}:`, {
          name: program.name,
          program_id: program.program_id,
          coach_id: program.coach_id,
          current_user_id: user.id,
          matches: program.coach_id === user.id
        });
      });
      
      // Fetch client data to get names for assigned clients
      const clientIds = data
        .filter(program => program.client_id)
        .map(program => program.client_id);
        
      let clientsData: any[] = [];
      if (clientIds.length > 0) {
        const { data: clientsResult } = await supabase
          .from('clients')
          .select('client_id, full_name')
          .in('client_id', clientIds);
          
        clientsData = clientsResult || [];
      }
      
      // Create a lookup table for client information
      const clientLookup = new Map();
      clientsData.forEach(client => {
        clientLookup.set(client.client_id, {
          id: client.client_id,
          name: client.full_name || 'Unnamed Client',
          avatar: '/placeholder.svg'
        });
      });

      // Transform the raw database programs into our Program type
      const transformedPrograms = data.map(row => {
        // Safely access the JSON data by first checking its type
        const programData = row.program_data;
        
        // Safely cast the ai_context to a more specific type for TypeScript
        const aiContext = row.ai_context as Record<string, any> | null;
        
        // Try to get values from AI context first, then fallback to program_data
        const name = 
          (aiContext && typeof aiContext === 'object' && aiContext.basic_info && typeof aiContext.basic_info === 'object' && 'name' in aiContext.basic_info) ? aiContext.basic_info.name : 
          (typeof programData === 'object' && programData !== null && 'name' in programData) ? programData.name : 
          row.name || 'Unnamed Program';
        
        const description = 
          (aiContext && typeof aiContext === 'object' && aiContext.basic_info && typeof aiContext.basic_info === 'object' && 'description' in aiContext.basic_info) ? aiContext.basic_info.description : 
          (typeof programData === 'object' && programData !== null && 'description' in programData) ? programData.description : 
          '';
        
        const duration = 
          (aiContext && typeof aiContext === 'object' && aiContext.basic_info && typeof aiContext.basic_info === 'object' && 'duration' in aiContext.basic_info) ? aiContext.basic_info.duration : 
          (typeof programData === 'object' && programData !== null && 'duration' in programData) ? programData.duration : 
          '';
        
        const tier = 
          (aiContext && typeof aiContext === 'object' && aiContext.basic_info && typeof aiContext.basic_info === 'object' && 'tier' in aiContext.basic_info) ? aiContext.basic_info.tier : 
          (typeof programData === 'object' && programData !== null && 'tier' in programData) ? programData.tier : 
          'Beginner';
        
        // Get assigned clients from program_data if available
        let assignedClients = [];
        
        // Try to get from AI context first
        if (aiContext && typeof aiContext === 'object' && aiContext.client && 
            typeof aiContext.client === 'object' && 'assigned_clients' in aiContext.client && 
            Array.isArray(aiContext.client.assigned_clients)) {
          assignedClients = aiContext.client.assigned_clients;
        } 
        // Then try from program_data
        else if (typeof programData === 'object' && programData !== null && 
                 'assigned_clients' in programData && Array.isArray(programData.assigned_clients)) {
          assignedClients = programData.assigned_clients.map((client: any) => ({
            id: client.id,
            name: client.name,
            avatar: client.avatar || '/placeholder.svg'
          }));
        }
        
        // If we have a client_id but no assigned clients in data structures, use our lookup
        if (row.client_id && assignedClients.length === 0 && clientLookup.has(row.client_id)) {
          assignedClients = [clientLookup.get(row.client_id)];
        }
        
        return {
          id: row.program_id,
          name,
          description,
          duration,
          tier,
          assignedClients
        };
      });

      console.log("Final transformed programs count:", transformedPrograms.length);
      setPrograms(transformedPrograms);
    } catch (error) {
      console.error('Error fetching programs:', error);
      toast({
        title: "Error fetching programs",
        description: "Failed to load programs. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  return {
    programs,
    isLoading,
    fetchPrograms
  };
}
